import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AIAssessmentGenerationModal from '../components/AIAssessmentGenerationModal';
import './AIAssessmentBuilder.css';
import apiClient from '../apiClient';

const AIAssessmentBuilder = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Information
    changeType: '',
    customChangeType: '',
    targetAudience: [],
    assessmentPurpose: '',
    
    // Assessment Configuration
    assessmentFormat: [],
    focusAreas: [],
    customFocusArea: '',
    
    // Assessment Parameters
    questionCount: 20,
    assessmentDepth: 'comprehensive',
    toneLanguage: 'formal',
    
    // Outcomes & Context
    intendedOutcomes: [],
    additionalContext: '',
    
    // Metadata
    assessmentTitle: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [generationError, setGenerationError] = useState(null);

  const changeTypeOptions = [
    'Organizational restructuring',
    'Technology implementation', 
    'Process improvement',
    'Culture change',
    'Merger/acquisition',
    'Policy change',
    'Other'
  ];

  const targetAudienceOptions = [
    'Executive leadership',
    'Middle management', 
    'Frontline staff',
    'Specific departments',
    'External stakeholders',
    'All employees',
    'Project team members',
    'Customer-facing staff'
  ];

  const assessmentPurposeOptions = [
    'Readiness assessment',
    'Impact analysis',
    'Training needs analysis',
    'Stakeholder engagement assessment',
    'Communication effectiveness',
    'Resistance analysis',
    'Progress tracking',
    'Risk assessment'
  ];

  const assessmentFormatOptions = [
    'Multiple choice',
    'Likert scale (1-5)',
    'Likert scale (1-7)', 
    'Open text responses',
    'Yes/No questions',
    'Ranking questions',
    'Scenario-based questions',
    'Matrix questions'
  ];

  const focusAreasOptions = [
    'Awareness of change',
    'Attitude toward change',
    'Change impact assessment',
    'Stakeholder involvement',
    'Leadership support',
    'Communication clarity',
    'Resource adequacy',
    'Training/support needs',
    'Confidence levels',
    'Timeline concerns',
    'Risk perception',
    'Benefits realization'
  ];

  const toneLanguageOptions = [
    { value: 'formal', label: 'Formal & Professional' },
    { value: 'informal', label: 'Informal & Conversational' },
    { value: 'technical', label: 'Technical & Detailed' },
    { value: 'non-technical', label: 'Non-technical & Simple' },
    { value: 'inclusive', label: 'Inclusive & Accessible' }
  ];

  const intendedOutcomesOptions = [
    'Identify change barriers',
    'Uncover training gaps',
    'Prioritize stakeholder concerns',
    'Gauge organizational readiness',
    'Track change progress',
    'Measure communication effectiveness',
    'Assess leadership support',
    'Evaluate resource needs',
    'Monitor resistance levels',
    'Validate change strategy'
  ];

  const totalSteps = 5;

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Basic Information
        if (!formData.changeType) newErrors.changeType = 'Change type is required';
        if (formData.changeType === 'Other' && !formData.customChangeType) {
          newErrors.customChangeType = 'Custom change type is required';
        }
        if (formData.targetAudience.length === 0) {
          newErrors.targetAudience = 'At least one target audience is required';
        }
        if (!formData.assessmentPurpose) newErrors.assessmentPurpose = 'Assessment purpose is required';
        break;
        
      case 2: // Assessment Configuration
        if (formData.assessmentFormat.length === 0) {
          newErrors.assessmentFormat = 'At least one assessment format is required';
        }
        if (formData.focusAreas.length === 0) {
          newErrors.focusAreas = 'At least one focus area is required';
        }
        break;
        
      case 3: // Assessment Parameters - no required fields, using defaults
        break;
        
      case 4: // Outcomes & Context
        if (formData.intendedOutcomes.length === 0) {
          newErrors.intendedOutcomes = 'At least one intended outcome is required';
        }
        break;
        
      case 5: // Final Details
        if (!formData.assessmentTitle) newErrors.assessmentTitle = 'Assessment title is required';
        if (!formData.description) newErrors.description = 'Assessment description is required';
        break;
        
      default:
        // No validation for unknown steps
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleMultiSelect = (field, option) => {
    const currentValues = formData[field] || [];
    const updatedValues = currentValues.includes(option)
      ? currentValues.filter(item => item !== option)
      : [...currentValues, option];
    
    handleInputChange(field, updatedValues);
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const generatePayload = () => {
    return {
      metadata: {
        title: formData.assessmentTitle,
        description: formData.description,
        createdAt: new Date().toISOString()
      },
      changeDetails: {
        type: formData.changeType === 'Other' ? formData.customChangeType : formData.changeType,
        targetAudience: formData.targetAudience,
        purpose: formData.assessmentPurpose,
        additionalContext: formData.additionalContext
      },
      assessmentConfiguration: {
        formats: formData.assessmentFormat,
        focusAreas: formData.focusAreas.concat(formData.customFocusArea ? [formData.customFocusArea] : []),
        questionCount: formData.questionCount,
        depth: formData.assessmentDepth,
        tone: formData.toneLanguage
      },
      objectives: {
        intendedOutcomes: formData.intendedOutcomes
      }
    };
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    // Clear previous results and show modal
    setGenerationResult(null);
    setGenerationError(null);
    setIsModalOpen(true);
    setIsLoading(true);
    
    try {
      const payload = generatePayload();
      
      // Save the AI assessment request using apiClient
      const response = await apiClient.post('/ai-assessments', payload);
      
      // apiClient (axios) returns data directly in response.data
      const responseData = response.data;
      
      // Create generation result for modal
      const result = {
        templateName: formData.assessmentTitle,
        questionCount: formData.questionCount,
        questionTypes: formData.assessmentFormat,
        focusAreas: formData.focusAreas,
        templateId: responseData.aiRequest?.generated_template_id,
        sampleQuestions: responseData.sampleQuestions || []
      };
      
      setGenerationResult(result);
    } catch (error) {
      console.error('Error submitting AI assessment:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Error submitting assessment configuration. Please check your connection and try again.';
      setGenerationError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setGenerationResult(null);
    setGenerationError(null);
  };

  const handleViewTemplate = () => {
    // Navigate to assessment template manager
    navigate('/settings/assessment-templates');
    handleCloseModal();
  };

  const handleTryAgain = () => {
    handleCloseModal();
    // handleSubmit will be called again when user clicks the generate button
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label>Type of Change *</label>
              <select 
                value={formData.changeType} 
                onChange={(e) => handleInputChange('changeType', e.target.value)}
                className={errors.changeType ? 'error' : ''}
              >
                <option value="">Select change type...</option>
                {changeTypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.changeType && <span className="error-text">{errors.changeType}</span>}
            </div>

            {formData.changeType === 'Other' && (
              <div className="form-group">
                <label>Custom Change Type *</label>
                <input 
                  type="text" 
                  value={formData.customChangeType}
                  onChange={(e) => handleInputChange('customChangeType', e.target.value)}
                  placeholder="Describe your specific type of change..."
                  className={errors.customChangeType ? 'error' : ''}
                />
                {errors.customChangeType && <span className="error-text">{errors.customChangeType}</span>}
              </div>
            )}

            <div className="form-group">
              <label>Target Audience * (Select all that apply)</label>
              <div className="checkbox-grid">
                {targetAudienceOptions.map(option => (
                  <label key={option} className="checkbox-item">
                    <input 
                      type="checkbox"
                      checked={formData.targetAudience.includes(option)}
                      onChange={() => handleMultiSelect('targetAudience', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors.targetAudience && <span className="error-text">{errors.targetAudience}</span>}
            </div>

            <div className="form-group">
              <label>Assessment Purpose *</label>
              <select 
                value={formData.assessmentPurpose} 
                onChange={(e) => handleInputChange('assessmentPurpose', e.target.value)}
                className={errors.assessmentPurpose ? 'error' : ''}
              >
                <option value="">Select assessment purpose...</option>
                {assessmentPurposeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.assessmentPurpose && <span className="error-text">{errors.assessmentPurpose}</span>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h3>Assessment Configuration</h3>
            
            <div className="form-group">
              <label>Assessment Format * (Select all that apply)</label>
              <div className="checkbox-grid">
                {assessmentFormatOptions.map(option => (
                  <label key={option} className="checkbox-item">
                    <input 
                      type="checkbox"
                      checked={formData.assessmentFormat.includes(option)}
                      onChange={() => handleMultiSelect('assessmentFormat', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors.assessmentFormat && <span className="error-text">{errors.assessmentFormat}</span>}
            </div>

            <div className="form-group">
              <label>Focus Areas / Dimensions * (Select all that apply)</label>
              <div className="checkbox-grid">
                {focusAreasOptions.map(option => (
                  <label key={option} className="checkbox-item">
                    <input 
                      type="checkbox"
                      checked={formData.focusAreas.includes(option)}
                      onChange={() => handleMultiSelect('focusAreas', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors.focusAreas && <span className="error-text">{errors.focusAreas}</span>}
            </div>

            <div className="form-group">
              <label>Custom Focus Area (optional)</label>
              <input 
                type="text" 
                value={formData.customFocusArea}
                onChange={(e) => handleInputChange('customFocusArea', e.target.value)}
                placeholder="Add a custom focus area if needed..."
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h3>Assessment Parameters</h3>
            
            <div className="form-group">
              <label>Number of Questions</label>
              <div className="slider-container">
                <input 
                  type="range"
                  min="5"
                  max="50"
                  value={formData.questionCount}
                  onChange={(e) => handleInputChange('questionCount', parseInt(e.target.value))}
                  className="slider"
                />
                <span className="slider-value">{formData.questionCount} questions</span>
              </div>
            </div>

            <div className="form-group">
              <label>Assessment Depth</label>
              <div className="radio-group">
                <label className="radio-item">
                  <input 
                    type="radio"
                    name="assessmentDepth"
                    value="brief"
                    checked={formData.assessmentDepth === 'brief'}
                    onChange={(e) => handleInputChange('assessmentDepth', e.target.value)}
                  />
                  <span>Brief (Quick overview)</span>
                </label>
                <label className="radio-item">
                  <input 
                    type="radio"
                    name="assessmentDepth"
                    value="comprehensive"
                    checked={formData.assessmentDepth === 'comprehensive'}
                    onChange={(e) => handleInputChange('assessmentDepth', e.target.value)}
                  />
                  <span>Comprehensive (Detailed analysis)</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Tone and Language</label>
              <select 
                value={formData.toneLanguage} 
                onChange={(e) => handleInputChange('toneLanguage', e.target.value)}
              >
                {toneLanguageOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h3>Outcomes & Context</h3>
            
            <div className="form-group">
              <label>Intended Outcomes * (Select all that apply)</label>
              <div className="checkbox-grid">
                {intendedOutcomesOptions.map(option => (
                  <label key={option} className="checkbox-item">
                    <input 
                      type="checkbox"
                      checked={formData.intendedOutcomes.includes(option)}
                      onChange={() => handleMultiSelect('intendedOutcomes', option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors.intendedOutcomes && <span className="error-text">{errors.intendedOutcomes}</span>}
            </div>

            <div className="form-group">
              <label>Additional Context</label>
              <textarea 
                value={formData.additionalContext}
                onChange={(e) => handleInputChange('additionalContext', e.target.value)}
                placeholder="Provide any additional context about your change initiative, organizational culture, timeline, known challenges, or special considerations..."
                rows="6"
              />
              <small>This context will help the AI generate more relevant and targeted assessment questions.</small>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h3>Final Details & Review</h3>
            
            <div className="form-group">
              <label>Assessment Title *</label>
              <input 
                type="text" 
                value={formData.assessmentTitle}
                onChange={(e) => handleInputChange('assessmentTitle', e.target.value)}
                placeholder="Enter a title for your AI-generated assessment..."
                className={errors.assessmentTitle ? 'error' : ''}
              />
              {errors.assessmentTitle && <span className="error-text">{errors.assessmentTitle}</span>}
            </div>

            <div className="form-group">
              <label>Assessment Description *</label>
              <textarea 
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Provide a brief description of what this assessment will measure..."
                rows="3"
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            <div className="review-summary">
              <h4>Configuration Summary</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <strong>Change Type:</strong> {formData.changeType === 'Other' ? formData.customChangeType : formData.changeType}
                </div>
                <div className="summary-item">
                  <strong>Target Audience:</strong> {formData.targetAudience.join(', ')}
                </div>
                <div className="summary-item">
                  <strong>Purpose:</strong> {formData.assessmentPurpose}
                </div>
                <div className="summary-item">
                  <strong>Question Count:</strong> {formData.questionCount}
                </div>
                <div className="summary-item">
                  <strong>Focus Areas:</strong> {formData.focusAreas.join(', ')}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="ai-assessment-builder">
      <div className="builder-header">
        <h2>AI Assessment Builder</h2>
        <p>Create a custom change management assessment powered by AI</p>
      </div>

      <div className="progress-indicator">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
        <div className="step-labels">
          <span className={currentStep >= 1 ? 'active' : ''}>Basic Info</span>
          <span className={currentStep >= 2 ? 'active' : ''}>Configuration</span>
          <span className={currentStep >= 3 ? 'active' : ''}>Parameters</span>
          <span className={currentStep >= 4 ? 'active' : ''}>Outcomes</span>
          <span className={currentStep >= 5 ? 'active' : ''}>Review</span>
        </div>
      </div>

      <div className="builder-content">
        {renderStepContent()}
      </div>

      <div className="builder-actions">
        <button 
          onClick={prevStep} 
          disabled={currentStep === 1}
          className="btn-secondary"
        >
          Previous
        </button>
        
        <button 
          onClick={() => navigate('/settings/assessment-templates')}
          className="btn-cancel"
        >
          Cancel
        </button>
        
        {currentStep < totalSteps ? (
          <button onClick={nextStep} className="btn-primary">
            Next
          </button>
        ) : (
          <button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className="btn-primary"
          >
            {isLoading ? 'Generating Assessment...' : 'Generate AI Assessment'}
          </button>
        )}
      </div>

      {/* AI Generation Modal */}
      <AIAssessmentGenerationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isLoading={isLoading}
        generationResult={generationResult}
        error={generationError}
        onViewTemplate={handleViewTemplate}
        onTryAgain={handleTryAgain}
      />
    </div>
  );
};

export default AIAssessmentBuilder; 
